"use client";

import { useEffect, useRef } from "react";
import { generateUUID } from "@/shared/utils/uuid";
import { useRealtimeAgentStore } from "@/shared/store/realtime-agent";
import { useServiceStatusStore } from "@/shared/store/service-status";
import { useAvatarStore } from "@/shared/store/avatar-store";
import { useVadStore } from "@/shared/store/vad";
import { toastActions } from "@/layouts/custom-avatar-page/toast";
import { AgentConnectionManager } from "./use-agent-connection";
import { getVoiceChunkQueue } from "./use-chunk-queue";
import { WsListenerEvent } from "../const/events";
import type {
  TextChunkMessage,
  ChunkMessage,
  SttResultMessage,
  ConnectedMessage,
  ErrorMessage,
  InterruptedMessage,
  AvatarStatusMessage,
  AvatarSetupCompleteMessage,
  MessageMode,
  LanguageCode,
  InactiveWarningMessage,
  DisconnectNoticeMessage,
  ReauthRequiredMessage,
} from "../types/websocket-messages";
import type { ServiceStatusMessage } from "../types/service-status-messages";
import * as handlers from "../lib/message-handlers";
import * as chunkProcessor from "../lib/chunk-processor";
import * as messageProcessor from "../lib/message-processor";
import { interruptActiveTurn, startLatestTurn } from "./turn-lifecycle";
import { ApiListenerRegistry } from "./api-listener-registry";
import { avatarSetupCoordinator } from "./avatar-setup-coordinator";
import { latestUserActionGuard } from "@/shared/lib/latest-user-action-guard";

/**
 * Agent WebSocket 메시지 송수신 훅 (리팩토링됨)
 *
 * 책임:
 * - 메시지 전송 (사용자 메시지, 중단 등)
 * - 메시지 수신 이벤트 리스너 등록
 * - 순수 함수로 처리 후 결과를 스토어에 반영
 */

const listenerRegistry = new ApiListenerRegistry<NonNullable<ReturnType<typeof AgentConnectionManager.getAPI>>>();

/**
 * 전역 리스너 상태 리셋
 * 재연결 시 새 API 인스턴스에 리스너를 다시 등록하기 위해 호출
 */
export function resetGlobalListeners() {
  console.log("🔄 [USE-AGENT-MESSAGES] 전역 리스너 상태 리셋");
  listenerRegistry.clear();
}

/**
 * 전역 리스너 강제 재등록
 * React 상태 배칭으로 인해 effect가 실행되지 않을 때 직접 호출
 */
export function forceRegisterListeners() {
  console.log("🔄 [USE-AGENT-MESSAGES] 강제 리스너 재등록 시도");

  // 이미 등록되어 있으면 스킵
  const currentApi = AgentConnectionManager.getAPI();
  if (currentApi && listenerRegistry.isBoundTo(currentApi)) {
    console.log("✅ [USE-AGENT-MESSAGES] 리스너 이미 등록됨");
    return;
  }

  const api = AgentConnectionManager.getAPI();
  if (!api || !api.isConnected) {
    console.warn("⚠️ [USE-AGENT-MESSAGES] API가 없거나 연결 안됨");
    return;
  }

  // 리스너 등록
  registerAllListeners(api);
}

/**
 * 모든 메시지 리스너 등록 (내부 함수)
 */
function registerAllListeners(api: ReturnType<typeof AgentConnectionManager.getAPI>) {
  if (!api) return;

  if (!listenerRegistry.bind(api)) {
    console.log("✅ [USE-AGENT-MESSAGES] 현재 API 리스너 이미 등록됨");
    return;
  }

  const addListener = <T,>(event: string, listener: (data: T) => void) => {
    listenerRegistry.add(event, listener);
  };

  console.log("🔧 [USE-AGENT-MESSAGES] 리스너 등록 시작");

  // 현재 컨텍스트 가져오기
  const getContext = () => ({
    currentTurnId: useRealtimeAgentStore.getState().currentTurnId,
    sessionId: useRealtimeAgentStore.getState().sessionId,
  });

  // 연결 확인만 처리한다. avatar_setup은 페이지 coordinator가 단독 소유한다.
  const onConnected = (data: ConnectedMessage) => {
    const result = handlers.handleConnectedMessage(data, getContext());
    if (result.success) {
      useRealtimeAgentStore.getState().setConnected(true);
    }
  };
  addListener<ConnectedMessage>(WsListenerEvent.CONNECTED, onConnected);

  // 리스너는 첫 open 뒤 등록되므로 이후 open은 동일 API의 자동 재연결이다.
  const onReconnectedOpen = () => {
    if (avatarSetupCoordinator.invalidate(api)) {
      useRealtimeAgentStore.getState().invalidateAvatarSetup();
    }
  };
  addListener<void>(WsListenerEvent.OPEN, onReconnectedOpen);

  // avatar_setup 완료 응답
  const onAvatarSetupComplete = (data: AvatarSetupCompleteMessage) => {
    console.log("✅ [AVATAR_SETUP_COMPLETE] 서버 응답:", data);

    const activeBinding = AgentConnectionManager.getBinding();
    const completionLanguage = data.language ?? activeBinding?.language;
    const voiceId = completionLanguage === 'en'
      ? data.avatar?.english_voice_id
      : completionLanguage === 'zh'
        ? data.avatar?.chinese_voice_id ?? data.avatar?.korean_voice_id
        : completionLanguage === 'ja'
          ? data.avatar?.japanese_voice_id ?? data.avatar?.korean_voice_id
          : data.avatar?.korean_voice_id;

    const accepted = avatarSetupCoordinator.complete(api, {
      agentId: data.agent_id,
      language: completionLanguage,
      realtimeProvider: data.realtime_provider,
      avatarId: data.avatar?.avatar_id,
      avatarType: data.avatar?.avatar_type,
      voiceId,
    });
    if (!accepted) {
      console.log("⚠️ [AVATAR_SETUP_COMPLETE] 현재 API/setup binding과 불일치 - 무시");
      return;
    }

    useRealtimeAgentStore.getState().setAvatarSetupComplete(true);
    console.log("✅ [AVATAR_SETUP_COMPLETE] setAvatarSetupComplete(true) 호출됨, agent_id:", data.agent_id);
  };
  addListener<AvatarSetupCompleteMessage>(WsListenerEvent.AVATAR_SETUP_COMPLETE, onAvatarSetupComplete);

  const onReauthRequired = (data: ReauthRequiredMessage) => {
    const activeAgentId = AgentConnectionManager.getBinding()?.agentId;
    if (data.agent_id && activeAgentId && data.agent_id !== activeAgentId) return;
    if (avatarSetupCoordinator.invalidate(api)) {
      useRealtimeAgentStore.getState().invalidateAvatarSetup();
    }
  };
  addListener<ReauthRequiredMessage>(WsListenerEvent.REAUTH_REQUIRED, onReauthRequired);

  // 텍스트 청크
  const onTextChunk = (data: TextChunkMessage) => {
    console.log("📨 [TEXT_CHUNK] 수신:", {
      text: data.text,
      is_final: data.is_final,
      turn_id: data.turn_id,
      sequence_num: data.sequence_num
    });

    const result = handlers.handleTextChunk(data, getContext());
    if (result.success && result.data) {
      const store = useRealtimeAgentStore.getState();
      store.addTextChunk(result.data);
      console.log("✅ [TEXT_CHUNK] 스토어에 추가됨, 현재 textChunks 길이:", store.textChunks.length);
    } else {
      console.warn("⚠️ [TEXT_CHUNK] 핸들러 처리 실패:", result.error);
    }
  };
  addListener<TextChunkMessage>(WsListenerEvent.TEXT_CHUNK, onTextChunk);

  // 음성 청크 (Queue Manager로 전달)
  const onChunk = (data: ChunkMessage) => {
    const result = handlers.handleChunk(data, getContext());
    if (result.success && result.data) {
      const store = useRealtimeAgentStore.getState();
      const queue = getVoiceChunkQueue();

      const processResult = chunkProcessor.processVoiceChunk(result.data, {
        chunks: [],
        messages: store.messages,
        currentTurnId: store.currentTurnId,
      });

      if (processResult) {
        store.setMessages(processResult.updatedMessages);
        store.setLoading(processResult.isLoading);
      }

      queue.enqueue(result.data);
    }
  };
  addListener<ChunkMessage>(WsListenerEvent.CHUNK, onChunk);

  // STT 결과
  const onSttResult = (data: SttResultMessage) => {
    const result = handlers.handleSttResult(data, getContext());
    if (result.success && result.data) {
      const store = useRealtimeAgentStore.getState();
      const processResult = messageProcessor.processSttResult(
        store.messages,
        result.data,
        store.currentTurnId
      );

      if (processResult) {
        store.setSttResult(result.data);
        store.setMessages(processResult.updatedMessages);
      }
    }
  };
  addListener<SttResultMessage>(WsListenerEvent.STT_RESULT, onSttResult);

  // 에러
  const onError = (data: ErrorMessage) => {
    const result = handlers.handleError(data, getContext());
    if (result.success && result.data) {
      const store = useRealtimeAgentStore.getState();
      store.setError(result.data);
      // Turnless errors are global diagnostics. They must not destroy a newer
      // active turn merely because an older server task failed late.
      if (data.turn_id && data.turn_id === store.currentTurnId) {
        store.setGreetingLoading(false);
        store.setAudioPlaying(false);
        store.setLoading(false);
        useAvatarStore.getState().setCurrentAnimation("listening");
        useVadStore.getState().setVadStatus("listening");
        store.clearCurrentTurn();
      }
    }
  };
  addListener<ErrorMessage>(WsListenerEvent.ERROR, onError);

  // 중단
  const onInterrupted = (data: InterruptedMessage) => {
    const result = handlers.handleInterrupted(data, getContext());
    if (result.success) {
      const store = useRealtimeAgentStore.getState();
      store.setGreetingLoading(false);
      store.setAudioPlaying(false);
      store.setLoading(false);
      store.setInterrupted(true);
      store.clearCurrentTurn();
    }
  };
  addListener<InterruptedMessage>(WsListenerEvent.INTERRUPTED, onInterrupted);

  // 아바타 상태
  const onAvatarStatus = (data: AvatarStatusMessage) => {
    handlers.handleAvatarStatus(data, getContext());
  };
  addListener<AvatarStatusMessage>("avatar_status", onAvatarStatus);

  // 서버 점검
  const onServerMaintenance = (data: ServiceStatusMessage) => {
    const result = handlers.handleServiceStatus(data, getContext());
    if (result.success && result.data) {
      api.setAutoReconnect(false);
      useServiceStatusStore.getState().setServiceStatus(result.data);
      api.disconnect();
    }
  };
  addListener<ServiceStatusMessage>("server_maintenance", onServerMaintenance);

  // 서비스 준비중
  const onServiceUnavailable = (data: ServiceStatusMessage) => {
    const result = handlers.handleServiceStatus(data, getContext());
    if (result.success && result.data) {
      api.setAutoReconnect(false);
      useServiceStatusStore.getState().setServiceStatus(result.data);
      api.disconnect();
    }
  };
  addListener<ServiceStatusMessage>("service_unavailable", onServiceUnavailable);

  // 접속 대기
  const onServiceStatus = (data: ServiceStatusMessage) => {
    const result = handlers.handleServiceStatus(data, getContext());
    if (result.success && result.data) {
      if (data.type === "service_status" && data.status === "busy") {
        useServiceStatusStore.getState().setServiceStatus(result.data);
      } else {
        useServiceStatusStore.getState().setServiceStatus(null);
      }
    }
  };
  addListener<ServiceStatusMessage>("service_status", onServiceStatus);

  // 관리자 공지
  const onAdminNotice = (data: ServiceStatusMessage) => {
    const result = handlers.handleServiceStatus(data, getContext());
    if (result.success && result.data) {
      api.setAutoReconnect(false);
      useServiceStatusStore.getState().setServiceStatus(result.data);
    }
  };
  addListener<ServiceStatusMessage>("admin_notice", onAdminNotice);

  // 비활성 경고 (서버에서 연결 해제 예고)
  const onInactiveWarning = (data: InactiveWarningMessage) => {
    console.log("⚠️ [INACTIVE_WARNING]:", data.message, "remaining_time:", data.remaining_time);

    // 동의 전(CoverModal/ConsentModal)에는 토스트 표시 안 함 - 자동 재연결만 처리
    const hasConsented = useRealtimeAgentStore.getState().hasConsented;
    if (!hasConsented) {
      console.log("⚠️ [INACTIVE_WARNING] 동의 전이므로 토스트 억제");
      return;
    }

    toastActions.show({
      type: 'warning',
      message: data.message,
      position: 'top',
      duration: 5000,
    });
  };
  addListener<InactiveWarningMessage>(WsListenerEvent.INACTIVE_WARNING, onInactiveWarning);

  // 연결 해제 알림 (서버에서 연결 해제됨) - 자동 재연결 트리거
  const onDisconnectNotice = (data: DisconnectNoticeMessage) => {
    console.log("🔌 [DISCONNECT_NOTICE]:", data.message, "reason:", data.reason);
    const store = useRealtimeAgentStore.getState();

    // 모달/토스트 없이 자동 재연결 트리거만 설정
    store.setDisconnectedByServer(true, data.reason);
    store.setShowReconnectModal(true); // useCustomAvatarPage/useKioskAvatarPage에서 자동 재연결 처리
  };
  addListener<DisconnectNoticeMessage>(WsListenerEvent.DISCONNECT_NOTICE, onDisconnectNotice);

  console.log("✅ [USE-AGENT-MESSAGES] 리스너 등록 완료", {
    등록된리스너수: listenerRegistry.listenerCount
  });
}

export function useAgentMessages() {
  // WebSocket 연결 상태 구독 - API가 준비될 때까지 기다림
  const isConnected = useRealtimeAgentStore((state) => state.isConnected);

  /**
   * 메시지 이벤트 리스너 설정
   */
  useEffect(() => {
    console.log("🚀 [USE-AGENT-MESSAGES] useEffect 실행됨", {
      registeredListenerCount: listenerRegistry.listenerCount,
      isConnected,
    });

    // 연결되지 않았으면 대기 (연결되면 이 useEffect가 다시 실행됨)
    if (!isConnected) {
      console.log("⏳ [USE-AGENT-MESSAGES] WebSocket 연결 대기 중...");
      return;
    }

    const api = AgentConnectionManager.getAPI();
    console.log("🔍 [USE-AGENT-MESSAGES] API 상태:", {
      hasAPI: !!api,
      apiType: api?.constructor.name,
      isConnected: api?.isConnected
    });

    if (!api) {
      console.warn("⚠️ [USE-AGENT-MESSAGES] API가 없어서 리스너 등록 불가");
      return;
    }

    if (listenerRegistry.isBoundTo(api)) {
      console.log("✅ [USE-AGENT-MESSAGES] 현재 API 리스너 이미 등록됨, 건너뜀");
      return;
    }

    // 공통 리스너 등록 함수 호출
    registerAllListeners(api);

    // Cleanup: 페이지 언로드 시에만 리스너 제거 (싱글톤이므로)
    // 컴포넌트 언마운트 시에는 제거하지 않음
  }, [isConnected]); // isConnected가 변경될 때마다 리스너 재등록 시도

  /**
   * 사용자 메시지 전송
   * displayMode, agentId를 store에서 가져와서 Backend로 전달
   */
  const sendMessage = (params: {
    mode: MessageMode;
    data: string;
    language?: LanguageCode;
    turnId?: string;
  }): boolean => {
    const api = AgentConnectionManager.getAPI();
    const store = useRealtimeAgentStore.getState();

    // displayMode, agentId를 store에서 가져오기 (Backend로 전달)
    const displayMode = store.displayMode;
    const agentId = store.agentId;

    const newTurnId = params.turnId ?? generateUUID();

    console.log("📤 [VOICE] 메시지 전송:", { displayMode, agentId, mode: params.mode });

    latestUserActionGuard.begin();
    const sent = startLatestTurn({
      state: store,
      getCurrentTurnId: () => useRealtimeAgentStore.getState().currentTurnId,
      turnId: newTurnId,
      sendInterrupt: (turnId) => api?.isConnected
        ? api.sendInterrupt(turnId)
        : false,
      sendMessage: (turnId) => api?.isConnected
        ? api.sendUserMessage({
            turnId,
            mode: params.mode,
            data: params.data,
            language: params.language,
            displayMode: displayMode,
            agentId: agentId || undefined,
          })
        : false,
    });
    if (!sent && !api?.isConnected) {
      console.warn("🚫 WebSocket이 연결되지 않아 새 턴을 idle로 정리했습니다");
    }
    return sent;
  };

  /**
   * 중단 메시지 전송
   */
  const sendInterrupt = (): boolean => {
    const api = AgentConnectionManager.getAPI();
    const store = useRealtimeAgentStore.getState();
    if (!store.currentTurnId) {
      console.warn("🚫 현재 턴 ID가 없습니다");
      return false;
    }

    if (!api?.isConnected) {
      console.warn("🚫 WebSocket이 연결되지 않았습니다");
      return false;
    }

    console.log("⏸️ 중단 메시지 전송");
    return interruptActiveTurn({
      state: store,
      getCurrentTurnId: () => useRealtimeAgentStore.getState().currentTurnId,
      sendInterrupt: (turnId) => api.sendInterrupt(turnId),
    });
  };

  /**
   * 아바타 상태 요청
   */
  const requestAvatarStatus = (): boolean => {
    const api = AgentConnectionManager.getAPI();
    if (!api?.isConnected) {
      console.warn("🚫 WebSocket이 연결되지 않았습니다");
      return false;
    }

    console.log("📷 아바타 상태 요청");
    return api.requestAvatarStatus();
  };

  return {
    sendMessage,
    sendInterrupt,
    requestAvatarStatus,
  };
}

/**
 * 청크 데이터 접근 훅 (Queue Manager 기반)
 */
export function useAgentChunksData() {
  const textChunks = useRealtimeAgentStore((state) => state.textChunks);
  const currentTurnId = useRealtimeAgentStore((state) => state.currentTurnId);
  const queue = getVoiceChunkQueue();

  return {
    textChunks,
    currentTurnId,
    voiceQueueState: queue.getState(), // 큐 상태 조회
  };
}

/**
 * STT 결과 접근 훅
 */
export function useAgentSttResultData() {
  return useRealtimeAgentStore((state) => state.currentSttResult);
}

/**
 * 에러 상태 접근 훅
 */
export function useAgentErrorData() {
  const lastError = useRealtimeAgentStore((state) => state.lastError);
  const isInterrupted = useRealtimeAgentStore((state) => state.isInterrupted);
  const isLoading = useRealtimeAgentStore((state) => state.isLoading);

  return { lastError, isInterrupted, isLoading };
}

/**
 * 로딩 상태 접근 훅
 */
export function useAgentLoadingData() {
  return useRealtimeAgentStore((state) => state.isLoading);
}

/**
 * 메시지 관리 헬퍼 훅
 */
export function useAgentMessageHelpers() {
  const store = useRealtimeAgentStore.getState();

  return {
    addUserMessage: (message: string, turnId?: string) => {
      const updated = messageProcessor.addUserMessage(store.messages, message, turnId);
      store.setMessages(updated);
    },
    setInitialGreeting: (greetingText: string) => {
      const updated = messageProcessor.setInitialGreeting(store.messages, greetingText);
      store.setMessages(updated);
    },
  };
}
